import * as React from 'react'
import { connect } from 'react-redux'
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next'
import { withRouter, RouteComponentProps } from 'react-router'
import MultiFileModal from './MultiFileModal'
import DeleteFileModal from './DeleteFileModal'
import Column from '@mv-submodules/inplant-components-fe/ui/components/Grid/Column'
import { Button } from '@mv-submodules/inplant-components-fe'
import IconComponent from '../../../../../inplant-components-fe/ui/components/MVIcon/Icon'
import { showNotification } from '@mv-submodules/mvlabs-components-fe/ui/components/Notification/Notification'
import FormComponentBuilder from '../FormComponent/FormComponentBuilder'
import {
  checklistMultiFileComponentUploads,
  checklistMultiFileGetUploaded,
} from '../../../../redux/actions/checklistEdit'
import { ChecklistComponentFileData, EditComponentMetadataError } from '../../../../redux/reducers/checklistEdit'
import { useState } from 'react'

export interface FileData {
  path: string
  blob?: Blob
}

export interface ComponentData {
  id: string
  optional: boolean
  type: {
    type: string
    values: string[]
  }
  defaultValue: Array<{ file: string }>
  lastValue: Array<{ file: string }>
}

export interface StateProps {
  componentData: ComponentData
  checklistInstanceId: string
  checkId: string
}

export interface DispatchProps {
  getFiles: (componentId: string, options?: { preview: boolean }) => Promise<Blob>
  updateFiles: (
    componentId: string,
    files: File[],
    options: { hideNotification?: boolean }
  ) => Promise<{ file: string }>
}

export interface OwnStateProps {
  fileModalShow: boolean
  fileModalPath: string | undefined
  deleteFileModalShow: boolean
  deleteFileModalFilePath: string | undefined
}

export interface OwnProps extends RouteComponentProps<any> {
  disabled?: boolean
  collapseHandler?: Function
}

export type Props = StateProps & DispatchProps & OwnProps & WithTranslation

const mapStateToProps = (state: any) => ({})

const mapDispatchToProps = (dispatch: Function): DispatchProps => {
  return {
    getFiles: (componentId, options) => dispatch(checklistMultiFileGetUploaded(componentId, options)),
    updateFiles: (componentId, file, options) =>
      dispatch(checklistMultiFileComponentUploads(componentId, file, options)),
  }
}

export class MultiFileComponent extends React.Component<Props, OwnStateProps> {
  constructor(props: Props) {
    super(props)
    this.state = {
      fileModalShow: false,
      fileModalPath: undefined,
      deleteFileModalShow: false,
      deleteFileModalFilePath: undefined,
    }
    this.updateFiles = this.updateFiles.bind(this)
    this.handleFileModalClose = this.handleFileModalClose.bind(this)
    this.handleFileModalOpen = this.handleFileModalOpen.bind(this)
    this.handleDeleteFileModalClose = this.handleDeleteFileModalClose.bind(this)
    this.handleDeleteFileModalOpen = this.handleDeleteFileModalOpen.bind(this)
    this.manageCollapsable()
    this.handleError = this.handleError.bind(this)
  }

  public componentDidMount() {
    if (this.props.componentData.lastValue.length > 0) {
      this.props.getFiles(this.props.componentData.id, { preview: true })
    }
  }

  private manageCollapsable() {
    if (
      this.props.componentData.lastValue.length > 0 &&
      this.props.componentData.optional &&
      this.props.collapseHandler
    ) {
      this.props.collapseHandler()
    }
  }

  private async updateFiles(event: React.ChangeEvent<HTMLInputElement>) {
    event.persist()
    const files = event.target.files
    if (!files) {
      return
    }

    this.props.updateFiles(this.props.componentData.id, Array.from(files), { hideNotification: true })
  }

  private handleFileModalOpen(filePath: string) {
    this.setState(prevState => ({ ...prevState, fileModalShow: true, fileModalPath: filePath }))
  }

  private handleFileModalClose() {
    this.setState(prevState => ({ ...prevState, fileModalShow: false }))
  }

  private handleDeleteFileModalOpen(filePath: string) {
    this.setState(prevState => ({ ...prevState, deleteFileModalShow: true, deleteFileModalFilePath: filePath }))
  }

  private handleDeleteFileModalClose() {
    this.setState(prevState => ({
      ...prevState,
      deleteFileModalShow: false,
      deleteFileModalFilePath: undefined,
    }))
  }

  private handleError(error: EditComponentMetadataError) {
    if (!error || error.type !== 'server') {
      return
    }

    if (error.message?.includes('Invalid Exif')) {
      showNotification({ type: 'danger', message: this.props.t('checklist.edit.errors.invalidExif') })
    }
  }

  public render() {
    return (
      <div className="multi-file-component">
        <FormComponentBuilder<ChecklistComponentFileData[]>
          component={this.props.componentData}
          onErrorReceived={this.handleError}
        >
          {({ className, updating, value: files, disabled, onBlur }) => {
            return (
              <>
                <MultiFileModal
                  show={this.state.fileModalShow}
                  onClose={this.handleFileModalClose}
                  filePath={this.state.fileModalPath}
                />
                <DeleteFileModal
                  componentId={this.props.componentData.id}
                  show={this.state.deleteFileModalShow}
                  onClose={this.handleDeleteFileModalClose}
                  filePath={this.state.deleteFileModalFilePath}
                />
                {updating ? (
                  <_Spinner />
                ) : (
                  <div className={`custom-file ${className} ${this.props.disabled ? 'hidden' : ''}`}>
                    <input
                      type={'file'}
                      multiple={true}
                      className={'custom-file-input'}
                      id={`${this.props.componentData.id}-multi-file`}
                      lang={'it'}
                      onChange={this.updateFiles}
                      disabled={disabled}
                      onBlur={onBlur}
                    />
                    <label className="custom-file-label" htmlFor="customFileLang">
                      {this.props.t('checklist.components.browse')}
                    </label>
                  </div>
                )}
                {files.length > 0 ? (
                  <div className="d-flex flex-row align-items-center">
                    {files.map((file, index) => (
                      <_Image
                        key={index}
                        file={file}
                        disabled={disabled}
                        onClick={() => this.handleFileModalOpen(file.path)}
                        onClickDelete={() => this.handleDeleteFileModalOpen(file.path)}
                      />
                    ))}
                  </div>
                ) : null}
              </>
            )
          }}
        </FormComponentBuilder>
      </div>
    )
  }
}

function _Spinner() {
  return (
    <div className="d-flex justify-content-center">
      <IconComponent icon={'circle-notch'} className="spinner" spin={true} size="1x" />
    </div>
  )
}

function _Image({
  disabled,
  file,
  onClick,
  onClickDelete,
}: {
  file: FileData
  onClick: () => void
  disabled: boolean
  onClickDelete: () => void
}) {
  const [error, setError] = useState(false)

  if (error) {
    return (
      <div className="multi-file-thumb">
        {!disabled ? <Button variant={'secondary'} onClick={onClickDelete} icon={'times'} /> : null}
        <_PreviewError />
      </div>
    )
  }

  return (
    <div className="multi-file-thumb">
      {!disabled ? <Button variant={'secondary'} onClick={onClickDelete} icon={'times'} /> : null}
      <_PreviewImage file={file} onClick={onClick} onError={() => setError(true)} />
    </div>
  )
}

function _PreviewError() {
  const { t } = useTranslation()
  return (
    <div>
      <div className="d-flex align-items-center justify-content-around border border-black">
        <Column xs={1}>
          <IconComponent icon={'exclamation-triangle'} />
        </Column>
        <Column xs={8}>
          <div className="text-left align-items-center">{t('checklist.components.previewImageError')}</div>
        </Column>
      </div>
    </div>
  )
}

function _PreviewImage({ file, onClick, onError }: { file: FileData; onClick: () => void; onError: () => void }) {
  return (
    <img
      className="img-fluid mx-auto d-block img-thumbnail"
      src={file?.blob ? URL.createObjectURL(file?.blob) : undefined}
      onClick={onClick}
      onError={onError}
    />
  )
}

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(MultiFileComponent)))
